import { memo, useCallback } from 'react';
import { FullScreenModalWrapper } from '../components/FullScreenModalWrapper/FullScreenModalWrapper';
import Bg from 'src/assets/images/jackpots/jackpot-win-modal-bg.webp';
import Logo from 'src/assets/page-banners/jackpots-banner-logo.webp';
import { Button } from 'src/components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import { useModalContext } from 'src/hooks/useModalContext';
import {
  AvailableBonusesTransactionsStatuses,
  BonusTransaction,
  getCurrencyIcon,
  makeFloat,
  useGetGame,
  useGetShortBalance,
  useUpdateBonusStatus
} from 'react-easyrocket';
import { useLocation } from 'react-router';
import { useGameHandleClick } from 'src/hooks/useGameHandleClick';
import classNames from 'classnames';
import { JackpotWinModalGame } from './components/JackpotWinModalGame';

type JackpotWinModalProps = {
  jackpot: BonusTransaction;
};

const _JackpotWinModal = () => {
  const { t } = useTranslation();
  const { closeModal, modalProps } = useModalContext<JackpotWinModalProps>();
  const { mutateAsync: updateBonusStatus, isPending: isLoading } = useUpdateBonusStatus();
  const { pathname } = useLocation();
  const { data } = useGetShortBalance();
  const isGamePage = /\bgame\b/.test(pathname);

  const { game } = useGetGame(
    { game_id: modalProps.jackpot?.game?.id || 0 },
    { enabled: Boolean(modalProps.jackpot?.game?.id) }
  );

  const { handleNavigateToGame } = useGameHandleClick({
    game
  });

  const handleBonus = useCallback(
    async (status: AvailableBonusesTransactionsStatuses) => {
      await updateBonusStatus({ bonus_id: modalProps?.jackpot.id, status });
      closeModal();
    },
    [closeModal, modalProps?.jackpot?.id, updateBonusStatus]
  );

  const hasActiveBonus = data?.bonusCasino > 0;
  const hasWager = modalProps?.jackpot.wager;
  const gamePageWithBonus = isGamePage && hasActiveBonus;
  const gamePageWithoutWager = isGamePage && hasWager === undefined;
  const notGamePageWithoutBonus = !isGamePage && !hasActiveBonus;
  const notGamePageWithBonus = !isGamePage && hasActiveBonus;
  const notGamePageWithoutWager = !isGamePage && hasWager === undefined;

  const showAddToQueue = (gamePageWithBonus || notGamePageWithBonus) && hasWager;
  const showDecline = hasWager;
  const buttonCount = (showAddToQueue ? 1 : 0) + (showDecline ? 1 : 0);

  const handlePlayAndActivateBonus = () => {
    if (notGamePageWithoutBonus || notGamePageWithBonus || notGamePageWithoutWager) {
      handleBonus('Activated');
      handleNavigateToGame();
    }
  };

  return (
    <FullScreenModalWrapper
      showCross={false}
      className="!justify-center !bg-[#0A0302B2] backdrop-blur-[10px]">
      <div className="w-full h-full fixed inset-0">
        <img
          draggable={false}
          src={Bg}
          className="w-full h-full object-cover"
          alt="jackpot win modal"
        />
      </div>

      <div className="relative z-[1] flex flex-col items-center max-w-[300px] 568:max-w-[600px] text-center mx-3">
        <div className="w-full">
          <img
            draggable={false}
            className="w-full h-full object-contain"
            src={Logo}
            alt="jackpot"
          />
        </div>

        <p className="mb-0.5 text-[white] font-bold uppercase leading-[14px]">
          {t('jackpots.yourWin')}
        </p>
        <p className="text-[white] text-5xl leading-[48px] 568:text-6xl 568:leading-[60px] font-extrabold">
          {getCurrencyIcon(data?.currency || 'EUR') + makeFloat(modalProps?.jackpot?.amount)}
        </p>

        <JackpotWinModalGame
          isGamePage={isGamePage}
          game={game}
          onClick={handlePlayAndActivateBonus}
        />

        <div
          className={classNames(
            'w-full grid gap-1',
            buttonCount === 2 ? '568:grid-cols-2 grid-cols-1' : 'grid-cols-1'
          )}>
          <Button
            loading={isLoading}
            onClick={() => {
              if (gamePageWithoutWager || notGamePageWithoutWager) {
                closeModal();
                return;
              }
              handleBonus('Activated');
            }}
            className="568:col-span-2 col-span-1"
            label={
              notGamePageWithoutBonus || notGamePageWithBonus
                ? t('jackpots.claim')
                : t('jackpots.claimPlay')
            }
            title={
              notGamePageWithoutBonus || notGamePageWithBonus
                ? t('jackpots.claim')
                : t('jackpots.claimPlay')
            }
          />
          {showAddToQueue && (
            <Button
              loading={isLoading}
              mode="button-secondary"
              label={t('jackpots.addToQueue')}
              title={t('jackpots.addToQueue')}
              onClick={() => handleBonus('Queue')}
            />
          )}
          {showDecline && (
            <Button
              loading={isLoading}
              mode="button-secondary"
              label={t('jackpots.decline')}
              title={t('jackpots.decline')}
              onClick={() => handleBonus('Rejected')}
            />
          )}
        </div>
      </div>
    </FullScreenModalWrapper>
  );
};

export const JackpotWinModal = memo(_JackpotWinModal);
